<template>
	<div class="search-product-results">
		<div class="results container">
			<div class="columns">
				<div class="column is-3 is-hidden-mobile" v-if="!hideFilters" role="complementary" aria-label="Product Filters">
					<div class="facet" v-for="facet in currentPage.facets" v-if="canShowFacet(facet)">
						<h2 class="title is-5">{{facet.name}} <a v-if="facet.name === 'Ingredients'" aria-label="See all ingredients" href="/ingredients">See All</a></h2>
						<hr/>
						<fieldset role="group" :aria-label="facet.name" v-if="facet.name !== 'Ingredients'">
							<legend>{{ facet.name }}</legend>
							<ul>
								<li v-for="(value, index) in facet.values" v-if="isFacetExpanded(facet) || (!isFacetExpanded(facet) && index < defaultFacetValuesShown)" >
									<b-checkbox v-model="facet.selected" :native-value="value.value" :disabled="value.count === 0" @input="search" :ref="getFacetRefName(facet, index)">{{value.label}}</b-checkbox>
								</li>
								<li class="see-more" v-if="!isFacetExpanded(facet) && facet.values.length > defaultFacetValuesShown">
									<a @click.prevent="clickExpandFacet(facet)" href="#" role="button" :aria-label="`See more ${facet.name} options`"><i class="far fa-plus"></i> See More</a>
								</li>
							</ul>
						</fieldset>
						<tag-search-input aria-label="Search ingredients to filter" v-model="facet.selected" :options="facet.values" v-else ref="ingredientSearch" @input="search"></tag-search-input>
					</div>
				</div>
				<div class="column" role="main" aria-label="Product Search Results">
					<div class="featured-products" v-if="hasFeaturedProducts">
						<h2 class="title is-4">{{ featuredProducts.title }}</h2>
						<hr/>
						<product-set :products="featuredProducts.content" column-classes="is-half-touch is-full-mobile is-one-third-desktop"></product-set>
					</div>
					<template v-if="hasFeaturedProducts">
						<h2 class="title is-4">{{ shopAllTitle }}</h2>
						<hr/>
					</template>
					<product-set :products="currentPage.data" :loading="loading" :show-add-to-cart="true" column-classes="is-half-touch is-full-mobile is-one-third-desktop">
						<template slot="firstTile" v-if="isDispensary && dispensary.whyThorne">
							<custom-product-tile headline="Why I choose Thorne" :description="dispensary.whyThorne">
								<template slot="description">
									<div class="dispensary-avatar">
										<figure class="image is-32x32" v-if="dispensary.avatarUrl">
										  	<img class="is-rounded" :src="dispensary.avatarUrl">
										</figure>
										<div class="avatar" v-else>
											<span class="initials">{{ dispensary.initials }}</span>
										</div>
									</div>
									<h2 class="title is-5 headline">Why I choose Thorne</h2>
									<p class="content">"{{ dispensary.whyThorne | truncate(120) }}"</p>
								</template>
							</custom-product-tile>
						</template>
						<template slot="randomTile" v-if="isDispensary && dispensary.goodHealth">
							<custom-product-tile headline="Good health means..." :description="dispensary.goodHealth">
								<template slot="description">
									<figure class="image is-32x32 dispensary-avatar" style="margin-bottom:.5rem" v-if="dispensary.avatarUrl">
									  	<img class="is-rounded" :src="dispensary.avatarUrl">
									</figure>
									<h2 class="title is-5 headline">Good health means...</h2>
									<p class="content">"{{ dispensary.goodHealth | truncate(120) }}"</p>
								</template>
							</custom-product-tile>
						</template>
						<template slot="tile3" v-if="tile3">
							<marketing-product-tile
								:icon-class="tile3.iconClass"
								:icon-color="tile3.iconColor"
								:title="tile3.title"
								:desc="tile3.desc"
								:link="tile3.link"
								:href="tile3.href"
								:background="tile3.background" 
							/>
						</template>
						<template slot="tile7" v-if="tile7">
							<marketing-product-tile
								:icon-class="tile7.iconClass"
								:icon-color="tile7.iconColor"
								:title="tile7.title"
								:desc="tile7.desc"
								:link="tile7.link"
								:href="tile7.href"
								:background="tile7.background" 
							/>
						</template>
					</product-set>
				</div>
			</div>
		</div>
		
		<ajax-pagination :items-total="currentPage.totalResults" :items-per-page="currentPage.pageSize" 
			:current-page="currentPage.pageNumber" :base-url="currentPage.nakedPageUrl" 
			:query-parameter="currentPage.pageFieldName" @page-changed="changePage"/>
	</div>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from "vuex";

import AjaxPagination from "../../AjaxPagination.vue";
import ProductSet from "../../ProductSet.vue";
import CustomProductTile from "../../CustomProductTile.vue";
import MarketingProductTile from "../../marketing/MarketingProductTile.vue";
import TagSearchInput from "../TagSearchInput.vue";
import ImageCardNew from "../../features/ImageCardNew.vue";

export default {
	name: "SearchProductResults",
	components: { AjaxPagination, ProductSet, CustomProductTile, MarketingProductTile, TagSearchInput, ImageCardNew },

  data() {
    return {
      defaultFacetValuesShown: 6,
    }
  },
  
	computed: {
		...mapState("search", ["currentPage", "hideFilters", "isDispensary", "dispensary", "featuredProducts", "featuredArticles"]),
		...mapGetters("search", ["hasFeaturedArticles", "hasFeaturedProducts", "shopAllTitle", "tile3", "tile7", "canShowFacet", "isFacetExpanded"])
	},
  
	methods: {
		...mapActions("search", ["expandFacet", "search", "changePage"]),
		...mapMutations("search", ["setIngredientSearchRef"]),

    clickExpandFacet(facet) {
      this.expandFacet(facet)
      //wait for the page to get updated
      this.$nextTick(() => {
        let itemRef = this.$refs[this.getFacetRefName(facet, this.defaultFacetValuesShown)];
        if (itemRef[0]) {
          itemRef[0].focus()
        }
      });
    },

    getFacetRefName(facet, index) {
      return facet.name.trim().replace(/\s+/g, '-') + '-' + index;
    }
	},
	
	mounted() {
		this.setIngredientSearchRef(this.$refs["ingredientSearch"]);
	}
};
</script>

<style lang="scss" scoped>

</style>